/**
 * Singleton UserManager accessible outside React's component tree.
 *
 * This lets the API layer (BaseApiClient) call `signinSilent()` when a 401
 * indicates the id_token has expired, rather than just re-reading the same
 * stale token from localStorage.
 */
import {type User, UserManager, WebStorageStateStore} from 'oidc-client-ts';
import {LocalUserManager} from './local-user-manager';

let instance: UserManager | undefined;

/**
 * Lazy-create and return the singleton UserManager.
 * Returns `undefined` during SSR / static build (no `window`).
 */
export function getUserManager(): UserManager | undefined {
  if (globalThis.window === undefined) {
    return undefined;
  }

  if (instance) {
    return instance;
  }

  // Password login (no Cognito): the same UserManager surface over a local token.
  if (
    (import.meta.env.VITE_AUTH_MODE as string | undefined)?.toLowerCase() ===
    'local-jwt'
  ) {
    instance = new LocalUserManager(globalThis.window.location.origin);
    return instance;
  }

  const userPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID as
    | string
    | undefined;
  const region = import.meta.env.VITE_COGNITO_REGION as string | undefined;
  const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID as string | undefined;

  if (!userPoolId || !region || !clientId) {
    return undefined;
  }

  // Use the current origin so the redirect works regardless of which domain
  // the user is on (e.g. app.dev.matic.systems or app.matic.systems).
  const appUrl = globalThis.window.location.origin;

  /* eslint-disable @typescript-eslint/naming-convention */
  instance = new UserManager({
    authority: `https://cognito-idp.${region}.amazonaws.com/${userPoolId}`,
    client_id: clientId,
    redirect_uri: `${appUrl}/`,
    post_logout_redirect_uri: `${appUrl}/logout`,
    response_type: 'code',
    scope: 'openid email profile',
    automaticSilentRenew: true,
    loadUserInfo: false,
    userStore: new WebStorageStateStore({store: globalThis.localStorage}),
  });
  /* eslint-enable @typescript-eslint/naming-convention */

  wireSessionInvalidation(instance);

  return instance;
}

/**
 * Drop the stored session when the token can no longer be kept alive.
 *
 * `automaticSilentRenew` renews the token before it expires, but that renewal
 * can lapse — the refresh token expires, the network blips, or (most commonly)
 * the renewal timer never fires because the tab was backgrounded / the machine
 * slept. When that happens oidc-client-ts leaves the now-stale user in
 * localStorage, so the app keeps believing it is authenticated with a dead
 * token (a "zombie session"). Reads self-heal via the API layer's 401 refresh,
 * but the user is never proactively bounced to re-auth.
 *
 * Removing the user on these terminal events flips `react-oidc-context` to
 * unauthenticated, which the route guard turns into a redirect to login.
 */
function wireSessionInvalidation(manager: UserManager): void {
  // Renewal attempt failed outright (e.g. refresh token expired/revoked).
  manager.events.addSilentRenewError(() => {
    void manager.removeUser();
  });

  // Token passed its expiry without a successful renew. Re-read the freshest
  // stored user before removing so a renewal that just landed isn't discarded.
  manager.events.addAccessTokenExpired(() => {
    void (async () => {
      const current = await manager.getUser();
      const expiresAtMs = (current?.expires_at ?? 0) * 1000;
      if (!current || expiresAtMs <= Date.now()) {
        await manager.removeUser();
      }
    })();
  });
}

// Concurrent deduplication: if multiple 401s fire at once, only one
// signinSilent() call is made; the rest await the same promise.
let inflightRefresh: Promise<User | null> | undefined;

/**
 * Actively refresh the token via `signinSilent()`.
 * Deduplicates concurrent callers — only one network request is made.
 */
export async function refreshTokenSilent(): Promise<User | null> {
  const manager = getUserManager();
  if (!manager) {
    return null;
  }

  if (inflightRefresh) {
    return inflightRefresh;
  }

  inflightRefresh = (async () => {
    try {
      return await manager.signinSilent();
    } finally {
      inflightRefresh = undefined;
    }
  })();

  return inflightRefresh;
}
